import { Pressable, StyleSheet, Text, TextInput, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  authoredLocaleOf,
  isAutoTranslated,
  type ProgramDraftWarning,
} from "@jackedlogs/shared";
import { LANGUAGE_ENDONYMS } from "@jackedlogs/i18n";

import { Badge } from "../primitives/Badge";
import { Icon } from "../Icon/Icon";
import type { UiTheme } from "../theme";
import type { ComposerStyles } from "./composerStyles";
import type { Composer } from "./useComposer";
import { useLanguageChip } from "./useLanguageChip";

// The program composer's meta card: name field, the weeks·days line, the
// draft/published/following status row, and the publish panel that opens
// under it. Split out of ProgramComposer for size (SP-COMPOSER-METABAND) —
// a pure move, so every value it reads stays owned by ComposerBody and
// arrives as a prop. The locked scheduler wiring (spine/editor/picker) was
// not touched.
//
// `publishOpen` is deliberately NOT owned here. ComposerEditLayout renders
// its `metaBand` prop on the two sides of one `wide ?` ternary — the wide
// arm at ComposerEditLayout.tsx:173, the narrow arm at :197 — so a component
// holding that state would be unmounted and remounted when the breakpoint
// flips, silently closing an open publish panel on a resize. Held in
// ComposerBody, the state sits above the ternary and survives it.
//
// `warningLabel` arrives as a prop for the same reason it isn't rebuilt
// here: the parent's warnings line renders the identical list, and a second
// copy of the emptyCell/pctNoMax wording is a divergence waiting to happen.
//
// PlanComposer has a meta band too, and it was checked rather than assumed:
// it shares only the name field (already shared, via cs.fieldLabel/cs.input),
// has no publish lifecycle at all, and carries a details disclosure
// (scheduledFor + description) this one doesn't. One component serving both
// would be a mode flag over two bodies that barely overlap, so the plan side
// is deliberately left where it is.
export function ComposerMetaBand({
  composer,
  cs,
  theme,
  showLiveEditHint,
  warnings,
  warningLabel,
  publishOpen,
  setPublishOpen,
}: {
  composer: Composer;
  cs: ComposerStyles;
  theme: UiTheme;
  // Pre-resolved by the parent: "this composer is editing an existing
  // program", which is what makes structural edits apply live.
  showLiveEditHint: boolean;
  warnings: readonly ProgramDraftWarning[];
  warningLabel: (w: ProgramDraftWarning) => string;
  publishOpen: boolean;
  setPublishOpen: (next: boolean | ((v: boolean) => boolean)) => void;
}) {
  const { t } = useTranslation();
  const p = theme.palette;
  const { draft, setDraft, busy, save } = composer;

  // PE-21 slice B. Sits beside the name because that is the first field
  // the language applies to, and it stays a folded chip so a coach who
  // writes in one language never has it in the way.
  // True only when the language currently being edited was produced by a
  // machine. A hand-written language, or a program with no translations
  // at all, shows nothing.
  const machineWritten =
    draft.editLocale !== null &&
    draft.editLocale !== undefined &&
    isAutoTranslated(draft.nameRaw, draft.editLocale);

  // ⭐ Switching into a language nobody has written yet empties every
  // field, which is correct (a fallback would let the coach type over the
  // original) but reads exactly like data loss. The safety is real and
  // lives in the draft; this is the line that says so.
  const untranslatedFrom =
    draft.editLocale && draft.name.trim().length === 0
      ? // ⛔ La lingua d'autore va passata: senza, `authoredLocaleOf`
        // sceglie la prima lingua nell'ordine dei locali supportati, e su
        // un programma già scritto in due lingue il messaggio nominerebbe
        // l'inglese come «originale» mentre l'originale è l'italiano.
        authoredLocaleOf(draft.nameRaw, draft.declaredAuthorLocale)
      : null;

  const language = useLanguageChip({ theme, draft, setDraft });

  return (
    <View style={cs.card}>
      <View style={styles.nameRow}>
        <Text style={cs.fieldLabel}>{t("program.name", { defaultValue: "Name" })}</Text>
        {/* PE-21 — a machine-written translation says so. Without this the
            coach can't tell their own words from a model's, and a later
            regeneration would overwrite them without anyone noticing.
            ⛔ TODO(premium-auto-translate): NOTHING sets `autoTranslated`
            yet — the generator is the premium slice — so this branch
            cannot render today. It ships now because the marker is
            per-language and can't be added later over written data, but
            do not read it as "done". It also hosts only the NAME: the
            other five authored fields carry the same flag and have no
            badge, which is the second half of the same slice. */}
        {machineWritten ? (
          <Badge
            theme={theme}
            tone="neutral"
            label={t("program.languageAuto", {
              defaultValue: "Machine translation",
            })}
          />
        ) : null}
        {language.trigger}
      </View>
      {language.tray}
      {untranslatedFrom && draft.editLocale ? (
        <Text style={cs.metaHint}>
          {t("program.languageNotYetWritten", {
            lang: LANGUAGE_ENDONYMS[draft.editLocale],
            original: LANGUAGE_ENDONYMS[untranslatedFrom],
            defaultValue:
              "No {{lang}} version yet. The {{original}} text stays where it is.",
          })}
        </Text>
      ) : null}
      <TextInput
        style={cs.input}
        value={draft.name}
        onChangeText={(v) => setDraft((d) => ({ ...d, name: v }))}
        placeholder={t("program.namePlaceholder", {
          defaultValue: "e.g. Powerbuilding 6-week",
        })}
        placeholderTextColor={p.ink3}
        accessibilityLabel={t("program.name", { defaultValue: "Name" })}
      />
      <Text style={cs.metaLine}>
        {t("composer.metaWeeks", { n: composer.weekCount, defaultValue: "{{n}} weeks" })}
        {"  ·  "}
        {t("composer.metaDays", { n: composer.dayCount, defaultValue: "{{n}} training days" })}
      </Text>
      {showLiveEditHint ? (
        <Text style={cs.metaHint}>
          {t("program.metaCyclesLive", {
            defaultValue: "Structural edits (days, weeks) apply as you make them",
          })}
        </Text>
      ) : null}

      <View style={cs.statusRow}>
        <Text style={cs.metaLine}>
          {draft.status === "draft"
            ? t("program.chipDraft", { defaultValue: "Draft" })
            : draft.status === "archived"
              ? t("program.chipPublished", { defaultValue: "Published" })
              : t("program.chipFollowed", { defaultValue: "Following" })}
        </Text>
        {draft.status === "draft" ? (
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: publishOpen }}
            disabled={busy}
            onPress={() => setPublishOpen((v) => !v)}
            style={cs.statusAction}
          >
            <Text style={cs.statusActionText}>
              {t("program.publish", { defaultValue: "Publish" })}…
            </Text>
          </Pressable>
        ) : draft.status === "archived" ? (
          <Pressable
            accessibilityRole="button"
            disabled={busy}
            onPress={() => void save("draft")}
            style={cs.statusAction}
          >
            <Text style={cs.statusActionText}>
              {t("program.unpublish", { defaultValue: "Back to draft" })}
            </Text>
          </Pressable>
        ) : null}
      </View>

      {publishOpen && draft.status === "draft" ? (
        <View style={cs.publishPanel}>
          {warnings.length > 0 ? (
            <>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <Icon name="warn" size={cs.checkTitle.fontSize} color={p.ink} />
                <Text style={cs.checkTitle}>
                  {t("composer.publishChecklist", {
                    defaultValue: "Open warnings (publishing anyway is fine)",
                  })}
                </Text>
              </View>
              {warnings.map((w, i) => (
                <Text key={i} style={cs.checkItem}>
                  · {warningLabel(w)}
                </Text>
              ))}
            </>
          ) : (
            <Text style={cs.checkItem}>
              {t("composer.publishClean", { defaultValue: "No open warnings." })}
            </Text>
          )}
          <View style={cs.checkActions}>
            <Pressable
              accessibilityRole="button"
              disabled={busy}
              onPress={() => {
                setPublishOpen(false);
                void save("archived");
              }}
              hitSlop={8}
              style={cs.checkFixBtn}
            >
              <Text style={cs.checkFixText}>
                {t("program.publish", { defaultValue: "Publish" })}
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              disabled={busy}
              onPress={() => {
                setPublishOpen(false);
                void save("active");
              }}
              hitSlop={8}
              style={cs.checkAnywayBtn}
            >
              <Text style={cs.checkAnywayText}>
                {t("program.publishAndFollow", { defaultValue: "Publish and follow" })}
              </Text>
            </Pressable>
          </View>
        </View>
      ) : null}
    </View>
  );
}

// The label keeps the row, the chip sits at its end: the language is a
// property OF the name field, not a control floating above the card.
const styles = StyleSheet.create({
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    // The badge's label is long in German ("Maschinelle Übersetzung") and
    // French: without wrapping it pushes the language chip past the card
    // edge on a narrow phone instead of dropping to a second line.
    flexWrap: "wrap",
  },
});
